import {
    ContentType,
    DirectoryEntryDtoDiscriminator,
    FileInfoResponse,
    FolderResponse,
} from '@kcftech/prometheus-client';
import { createContentPath, createScopedId } from '@kcftech/prometheus-files-sdk';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { usePagedPrefetch } from 'custom_hooks/data_hooks/data_source/usePagedPrefetch';
import { toRequiredScope } from 'pages/Settings/FileLibrary/path';
import { buildOrderBy, toDataSourceResult } from 'utils/query_v3';

// Union type for polymorphic responses
export type DirectoryEntry = FolderResponse | FileInfoResponse;

export type DirectoryFilter = Readonly<{
    folderPath?: string | undefined;
    folderId?: string | undefined;
    locationId?: string | undefined;
    corporationId?: string | undefined;
    nameSearch?: string | undefined;
    fileTypes?: ContentType[];
    recursive?: boolean;
}>;

export type UseDirectoryDataSourceOptions = BaseDataSourceOptions<
    DirectoryEntry,
    DirectoryFilter
>;

const getDirectoryEntries: DataSourceQuery<DirectoryEntry, DirectoryFilter> = ({
    filter: {
        folderPath,
        folderId,
        nameSearch,
        locationId,
        corporationId,
        fileTypes,
        recursive = false,
    },
    sort,
    page,
    pageLimit,
}) => {
    const orderBy = buildOrderBy(sort);

    const scope = toRequiredScope(corporationId, locationId);

    return toDataSourceResult(
        sdClient.listDirectoryEntries({
            // filter: Filter.and(fileTypes ? Filter.in('EntryType', fileTypes) : undefined),
            folderIdentifier: folderId
                ? createScopedId({ ...scope, fileId: folderId })
                : createContentPath({
                      ...scope,
                      filePath: folderPath ?? '/',
                  }),
            page,
            pageLimit,
            orderBy,
            nameSearch,
            recursive,
            fileTypes,
        }),
    );
};

export type DirectoryPagedDataSource = PagedDataSource<DirectoryEntry, DirectoryFilter>;

export function usePagedDirectoryDataSource({
    ...options
}: UseDirectoryDataSourceOptions): DirectoryPagedDataSource {
    return usePagedDataSource({
        staleTime: 10 * 60 * 1000, // 10 mins
        ...options,
        query: getDirectoryEntries,
    });
}

export type DirectoryInfiniteDataSource = InfiniteDataSource<
    DirectoryEntry,
    DirectoryFilter
>;

export function useInfiniteDirectoryDataSource({
    ...options
}: UseDirectoryDataSourceOptions): DirectoryInfiniteDataSource {
    return useInfiniteDataSource({
        ...options,
        query: getDirectoryEntries,
    });
}

export function useDirectoryPrefetch(dataSource: DirectoryPagedDataSource) {
    return usePagedPrefetch(dataSource, getDirectoryEntries);
}

// Type guards for polymorphic types
export function isFolderResponse(entry: DirectoryEntry): entry is FolderResponse {
    return entry.Discriminator === DirectoryEntryDtoDiscriminator.Folder;
}

export function isFileInfoResponse(entry: DirectoryEntry): entry is FileInfoResponse {
    return entry.Discriminator === DirectoryEntryDtoDiscriminator.File;
}
